import moment from "moment";
import { useState } from "react";

export const CreateNewClient = ({ handleClientSubmit }) => {
  const [isExpirationDateValid, setIsExpirationDateValid] = useState(true);
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    companyName: "",
    projectName: "",
    companyAddress: "",
    companyID: "",
    projectID: "",
    registrationDate: "",
    expirationDate: "",
  });

  const [expirationOption, setExpirationOption] = useState("");

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Update the form data
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // Handle expiration option selection
    if (name === "expirationOption") {
      handleExpirationOption(value);
    }

    // If changing the registrationDate field and an expiration option is selected, update expiration date
    if (name === "registrationDate" && expirationOption !== "SetManually") {
      updateExpirationDate(value);
    }

    // If changing the expirationDate field, perform validation
    if (name === "expirationDate") {
      validateExpirationDate(value);
    }
  };

  // Update expiration date based on registration date and selected option
  const updateExpirationDate = (registrationDate) => {
    const momentRegistrationDate = moment(registrationDate, "YYYY-MM-DD");
    let expirationDate;

    switch (expirationOption) {
      case "1Year":
        expirationDate = momentRegistrationDate
          .clone()
          .add(1, "year")
          .format("YYYY-MM-DD");
        break;
      case "2Years":
        expirationDate = momentRegistrationDate
          .clone()
          .add(2, "years")
          .format("YYYY-MM-DD");
        break;
      case "3Years":
        expirationDate = momentRegistrationDate
          .clone()
          .add(3, "years")
          .format("YYYY-MM-DD");
        break;
      default:
        expirationDate = "";
    }

    setFormData((prevData) => ({
      ...prevData,
      expirationDate,
    }));
    validateExpirationDate(expirationDate); // Validate the new expiration date
  };

  // Handle expiration option selection
  const handleExpirationOption = (value) => {
    setExpirationOption(value);
    const registrationDate = moment(formData.registrationDate, "YYYY-MM-DD");
    let expirationDate;
    switch (value) {
      case "1Year":
        expirationDate = registrationDate
          .clone()
          .add(1, "year")
          .format("YYYY-MM-DD");
        break;
      case "2Years":
        expirationDate = registrationDate
          .clone()
          .add(2, "years")
          .format("YYYY-MM-DD");
        break;
      case "3Years":
        expirationDate = registrationDate
          .clone()
          .add(3, "years")
          .format("YYYY-MM-DD");
        break;
      case "SetManually":
        expirationDate = "";
        break;
      default:
        expirationDate = "";
    }
    setFormData((prevData) => ({
      ...prevData,
      expirationDate,
    }));
    validateExpirationDate(expirationDate); // Validate the new expiration date
  };

  // Validate expiration date
  const validateExpirationDate = (date) => {
    const registrationDate = moment(formData.registrationDate, "YYYY-MM-DD");
    const expirationDate = moment(date, "YYYY-MM-DD");
    if (expirationDate.isBefore(registrationDate)) {
      setIsExpirationDateValid(false);
    } else {
      setIsExpirationDateValid(true);
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();

    // Format the date values before submitting
    const formattedFormData = {
      ...formData,
      registrationDate: formatDate(formData.registrationDate),
      expirationDate: formatDate(formData.expirationDate),
    };

    handleClientSubmit(formattedFormData);

    // Reset the form fields
    // Reset the form fields
    setFormData({
      email: "",
      firstName: "",
      lastName: "",
      phoneNumber: "",
      companyName: "",
      projectName: "",
      companyAddress: "",
      companyID: "",
      projectID: "",
      registrationDate: "",
      expirationDate: "",
    });
    setExpirationOption("SetManually");
    setIsExpirationDateValid(true); // Reset validation state
  };

  // Function to format date to MM-DD-YYYY
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  return (
    <form onSubmit={handleSubmit} className="row g-3">
      <h2 className="mb-4">Create New Client</h2>

      {/* First Name and Last Name */}
      <div className="col-md-6">
        <label htmlFor="firstName" className="form-label">
          First Name:
        </label>
        <input
          type="text"
          className="form-control"
          id="firstName"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          required
        />
      </div>
      <div className="col-md-6">
        <label htmlFor="lastName" className="form-label">
          Last Name:
        </label>
        <input
          type="text"
          className="form-control"
          id="lastName"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          required
        />
      </div>

      {/* Phone Number and Email */}
      <div className="col-md-6">
        <label htmlFor="phoneNumber" className="form-label">
          Phone Number:
        </label>
        <input
          type="text"
          className="form-control"
          id="phoneNumber"
          name="phoneNumber"
          value={formData.phoneNumber}
          onChange={handleChange}
          required
        />
      </div>
      <div className="col-md-6">
        <label htmlFor="email" className="form-label">
          Email:
        </label>
        <input
          type="email"
          className="form-control"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
        />
      </div>

      {/* Company Name and Company Address */}
      <div className="col-md-6">
        <label htmlFor="companyName" className="form-label">
          Company Name:
        </label>
        <input
          type="text"
          className="form-control"
          id="companyName"
          name="companyName"
          value={formData.companyName}
          onChange={handleChange}
          required
        />
      </div>

      <div className="col-md-6">
        <label htmlFor="projectName" className="form-label">
          Project Name:
        </label>
        <input
          type="text"
          className="form-control"
          id="projectName"
          name="projectName"
          value={formData.projectName}
          onChange={handleChange}
          required
        />
      </div>
      <div className="col-md-6">
        <label htmlFor="companyAddress" className="form-label">
          Company Address:
        </label>
        <input
          type="text"
          className="form-control"
          id="companyAddress"
          name="companyAddress"
          value={formData.companyAddress}
          onChange={handleChange}
          required
        />
      </div>

      {/* Company ID and Project ID */}
      <div className="col-md-6">
        <label htmlFor="companyID" className="form-label">
          Company ID:
        </label>
        <input
          type="text"
          className="form-control"
          id="companyID"
          name="companyID"
          value={formData.companyID}
          onChange={handleChange}
          required
        />
      </div>
      <div className="col-md-6">
        <label htmlFor="projectID" className="form-label">
          Project ID:
        </label>
        <input
          type="text"
          className="form-control"
          id="projectID"
          name="projectID"
          value={formData.projectID}
          onChange={handleChange}
          required
        />
      </div>

      {/* Registration Date */}
      <div className="col-md-6">
        <label htmlFor="registrationDate" className="form-label">
          Registration Date:
        </label>
        <input
          type="date"
          className="form-control"
          id="registrationDate"
          name="registrationDate"
          value={formData.registrationDate}
          onChange={handleChange}
          required
        />
      </div>

      {/* Expiration Option Select */}
      <div className="col-md-6">
        <label htmlFor="expirationOption" className="form-label">
          Expiration Option:
        </label>
        <select
          className="form-select"
          id="expirationOption"
          name="expirationOption"
          value={expirationOption}
          onChange={handleChange}
          disabled={!formData.registrationDate}
        >
          <option value="SetManually">Set Date Manually</option>
          <option value="1Year">1 Year</option>
          <option value="2Years">2 Years</option>
          <option value="3Years">3 Years</option>
        </select>
      </div>

      {/* Expiration Date */}
      <div className="col-md-6">
        <label htmlFor="expirationDate" className="form-label">
          Expiration Date:
        </label>
        <input
          type="date"
          className="form-control"
          id="expirationDate"
          name="expirationDate"
          value={formData.expirationDate}
          onChange={handleChange}
          required={expirationOption === "SetManually"}
          disabled={
            expirationOption !== "SetManually" && !formData.registrationDate
          }
        />
        {!isExpirationDateValid && (
          <span className="text-danger">
            Expiration Date should be after Registration Date
          </span>
        )}
      </div>
      {/* Submit Button */}
      <div className="col-12">
        <button
          className="btn btn-primary w-100"
          disabled={!isExpirationDateValid || !formData.expirationDate}
          type="submit"
        >
          Submit
        </button>
      </div>
    </form>
  );
};
